import { FunctionComponent, useMemo } from 'react';
import {
	Box,
	Button,
	H2,
	HStack,
	Text,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from 'components/common/BackButton/BackButton';
import { LabelAndValue } from 'components/common/LabelAndValue/LabelAndValue';
import { toDefaultDateFormatOrNotAvailable } from 'utils/dateUtils';
import SectionDivider from 'components/common/SectionDivider';
import { BUNDLES_GET_BUNDLE } from 'apiRoutes';
import { getSubscriptionStatusColor } from 'domain/subscription/interfaces/Subscription';
import { SubscriptionBundle } from 'domain/subscription/interfaces/SubscriptionBundle';
import { RequestLoader } from 'components/common/Loader';
import StyledLink from 'components/common/Link';
import { RequestStatus } from 'types/RequestStatus';
import NotFound from 'components/common/NotFound/NotFound';
import { subscriptionTableColumns } from 'components/subscription/subscriptionTableColumns';
import { useGlobalContext } from 'components/common/GlobalContext/GlobalContext';
import BundledSubscriptionsTable from 'components/subscription/BundledSubscriptionsTable';
import { bundledSubscriptionTableColumns } from './bundleSubscriptionsColumns';
import { ProductIcon } from 'components/common/Product/ProductIcon/ProductIcon';
import { getProductConfig } from 'domain/product/productMap';
import { BundleActions } from './BundleActions/BundleActions';
import { ConditionallyDisabled } from 'components/common/ConditionallyDisabled/ConditionallyDisabled';
import useAuthRoles from 'hooks/useAuthRoles';
import { AdminRole } from 'domain/account/enums';
import { useIsCompanyPayingCustomer } from 'hooks/useIsCompanyPayingCustomer';

const BundlePage: FunctionComponent = () => {
	const { id } = useParams();
	if (!id) return null;

	const navigate = useNavigate();
	const { getMinimalAreaRepresentation, getAddonByCode } = useGlobalContext();

	const {
		data: bundle,
		getData: getBundle,
		status,
		error,
	} = useGetOnMount<SubscriptionBundle>(
		`${BUNDLES_GET_BUNDLE}?bundleId=${id}`,
	);

	const columns = useMemo(
		() => [
			bundledSubscriptionTableColumns.plan(bundle?.productId),
			bundledSubscriptionTableColumns.addons(bundle?.productId),
			subscriptionTableColumns.status(),
			subscriptionTableColumns.startDate(),
			subscriptionTableColumns.endDate(),
			subscriptionTableColumns.seats(),
			subscriptionTableColumns.type(), // may need to handle inheritance from bundle
			subscriptionTableColumns.expandedIndicator(),
		],
		[bundle?.subscriptions],
	);

	if (status === RequestStatus.NOT_FOUND) {
		return <NotFound title="Bundle Not Found" error={error} />;
	}

	const { adminIsRestrictedToRole: isRestrictedToRole } = useAuthRoles();
	const { isPayingCustomer: companyIsPayingCustomer } =
		useIsCompanyPayingCustomer(bundle?.companyId);

	const isAddSubscriptionDisabled =
		isRestrictedToRole(AdminRole.SALES) && !companyIsPayingCustomer;

	return (
		<RequestLoader h="500px" status={status}>
			<Box bg="bgMainContainer">
				<BackButton />
				<Box bg="bgMainContainer">
					<VStack gap="xl">
						<HStack
							fullWidth
							align="center"
							gap="lg"
							justify="space-between"
						>
							<H2>Bundle Details</H2>
							<BundleActions
								bundle={bundle}
								onBundleUpdated={getBundle}
							/>
						</HStack>
						<LabelAndValue
							label="Description"
							value={bundle?.description || '-'}
						/>
						<HStack fullWidth gap="xl">
							<LabelAndValue
								minWidth="30%"
								label="Type"
								value={bundle?.type}
							/>
							<LabelAndValue
								minWidth="30%"
								label="Status"
								textColor={getSubscriptionStatusColor(
									bundle?.status,
								)}
								value={bundle?.status}
							/>
						</HStack>
						<HStack fullWidth gap="xl">
							<LabelAndValue
								minWidth="30%"
								label="Start Date"
								value={toDefaultDateFormatOrNotAvailable(
									bundle?.startDate,
									'N/A',
									'DD MMM YYYY',
								)}
							/>
							<LabelAndValue
								minWidth="30%"
								label="End Date"
								value={toDefaultDateFormatOrNotAvailable(
									bundle?.endDate,
									'N/A',
									'DD MMM YYYY',
								)}
							/>
						</HStack>
					</VStack>
					<SectionDivider />
					<VStack gap="xl">
						<H2>Ownership</H2>
						<HStack fullWidth gap="xl">
							<LabelAndValue
								minWidth="30%"
								label="Company"
								value={
									bundle?.companyId ? (
										<StyledLink
											to={`/companies/${bundle.companyId}`}
										>
											{bundle.companyName}
										</StyledLink>
									) : (
										'-'
									)
								}
							/>
							<LabelAndValue
								minWidth="30%"
								label="Seats (Assigned/Paid for)"
								value={`${bundle?.occupiedSeats ?? '-'}/${
									bundle?.seats ?? '-'
								}`}
							/>
						</HStack>
					</VStack>
					<SectionDivider />
					<VStack gap="xl">
						<H2>Permissions</H2>
						<HStack align="center">
							<ProductIcon productId={bundle?.productId} />
							<Text>
								{getProductConfig(bundle?.productId)?.name}
							</Text>
						</HStack>
						<HStack>
							<LabelAndValue
								label="Areas"
								value={
									<VStack gap="xxxs">
										{getMinimalAreaRepresentation(
											bundle?.productId,
											bundle?.permissions?.countries,
										).map((area) => (
											<span key={area?.code}>
												{area?.name}
											</span>
										))}
									</VStack>
								}
							/>
							<LabelAndValue
								label="Addons"
								value={
									<VStack gap="xxxs">
										{(
											bundle?.permissions?.applicationPermissions.find(
												(ap) =>
													ap.applicationId ===
													bundle?.productId,
											)?.addons ?? []
										).map((addon) => (
											<span key={addon}>
												{getAddonByCode(
													bundle?.productId,
													addon,
												)?.name || addon}
											</span>
										))}
									</VStack>
								}
							/>
						</HStack>
					</VStack>
					<SectionDivider />
					<VStack>
						<BundledSubscriptionsTable
							bundle={bundle}
							columns={columns}
							requestStatus={status}
							subscriptions={bundle?.subscriptions ?? []}
							onUserUnassigned={() => {}}
						/>

						<ConditionallyDisabled
							disableIf={isAddSubscriptionDisabled}
							tooltip={
								'Sales role can only create a subscription for companies that are paying customers'
							}
						>
							<Button
								text="Add subscription"
								onClick={() =>
									navigate(
										`/subscriptions/create?bundleId=${id}&companyId=${bundle.companyId}`,
									)
								}
							/>
						</ConditionallyDisabled>
					</VStack>
				</Box>
			</Box>
		</RequestLoader>
	);
};

export default BundlePage;
